import * as React from 'react';

import ActivityFocusFilters from './activity_focus_filters';
import ActivityClassificationFilters from './activity_classification_filters';
import ContentPartnerFilters from './content_partner_filters';
import TypingRequirementsFilters from './typing_requirements_filters';
import EarlyAccessFilters from './early_access_filters';
import ELLFilters from './ell_filters';
import FlagFilters from './flag_filters';
import { Activity, ActivityFocusEditor, Topic } from './interfaces';
import StaffActivityFocusFilters from './staff_activity_focus_filters';
import TargetGradeRangeFilters from './target_grade_range_filters';
import TopicFilters from './topic_filters';

import { EVIDENCE_BETA_FLAGS, } from '../../../../../../constants/flagOptions';

interface FilterColumnProps {
  activities: Activity[],
  filteredActivities: Activity[],
  calculateNumberOfFilters: () => number,
  resetAllFilters: () => void,
  activityClassificationFilters: string[],
  handleActivityClassificationFilterChange: (activityClassificationFilters: string[]) => void,
  ellFilters: string[],
  handleELLFilterChange: (ellFilters: number[]) => void,
  targetGradeRangeFilters: string[],
  handleTargetGradeRangeFilterChange: (targetGradeRangeFilters: string[]) => void,
  activityFocusFilters: number[],
  handleActivityFocusFilterChange: (activityFocusFilters: number[]) => void,
  filterActivities: (ignoredKey?: string) => Activity[],
  contentPartnerFilters: number[],
  handleContentPartnerFilterChange: (contentPartnerFilters: number[]) => void,
  topicFilters: number[],
  handleTopicFilterChange: (topicFilters: number[]) => void,
  savedActivityFilters: number[],
  handleSavedActivityFilterChange: () => void,
  savedActivityIds: number[],
  flagFilters: string[],
  handleFlagFilterChange: () => void,
  earlyAccessFilters: string[],
  handleEarlyAccessFilterChange: (earlyAccessFilters: string[]) => void,
  flagset: string,
  isStaff?: boolean,
  activityFocusEditor?: ActivityFocusEditor,
  topics: Topic[],
  typingRequirementFilters: string[];
  handleTypingRequirementFilterChange: (typingRequirementFilters: string[]) => void;
}

const FilterColumn = ({
  activities,
  filteredActivities,
  filterActivities,
  calculateNumberOfFilters,
  resetAllFilters,
  activityClassificationFilters,
  handleActivityClassificationFilterChange,
  ellFilters,
  handleELLFilterChange,
  targetGradeRangeFilters,
  handleTargetGradeRangeFilterChange,
  handleActivityFocusFilterChange,
  activityFocusFilters,
  contentPartnerFilters,
  handleContentPartnerFilterChange,
  topicFilters,
  handleTopicFilterChange,
  savedActivityFilters,
  handleSavedActivityFilterChange,
  savedActivityIds,
  isStaff,
  flagFilters,
  handleFlagFilterChange,
  activityFocusEditor,
  handleEarlyAccessFilterChange,
  earlyAccessFilters,
  handleTypingRequirementFilterChange,
  typingRequirementFilters,
  flagset,
  topics,
}: FilterColumnProps) => {
  const numberOfFilters = calculateNumberOfFilters()
  const clearAllButton = numberOfFilters ? <button className="interactive-wrapper clear-filter focus-on-light" onClick={resetAllFilters} type="button">Clear all filters</button> : <span />
  const filterCount = numberOfFilters ? `${numberOfFilters} filter${numberOfFilters === 1 ? '' : 's'} • ` : ''

  let earlyAccessFilterSection
  let flagFilterSection
  let activityFocusFilterSection = (<ActivityFocusFilters
    activities={activities}
    activityFocusFilters={activityFocusFilters}
    filterActivities={filterActivities}
    handleActivityFocusFilterChange={handleActivityFocusFilterChange}
  />)

  if (isStaff) {
    flagFilterSection = (<FlagFilters
      activities={activities}
      filterActivities={filterActivities}
      flagFilters={flagFilters}
      handleFlagFilterChange={handleFlagFilterChange}
    />)
    // When isStaff is true, prefer an editor-aware section; otherwise the read-only
    // ActivityFocusFilters initialized above is rendered as the fallback.
    if (activityFocusEditor) {
      activityFocusFilterSection = (<StaffActivityFocusFilters
        activityFocusEditor={activityFocusEditor}
        filterActivities={filterActivities}
      />)
    }
  }

  if (EVIDENCE_BETA_FLAGS.includes(flagset)) {
    earlyAccessFilterSection = (
      <EarlyAccessFilters
        activities={activities}
        earlyAccessFilters={earlyAccessFilters}
        filterActivities={filterActivities}
        flagset={flagset}
        handleEarlyAccessFilterChange={handleEarlyAccessFilterChange}
      />
    )
  }

  return (
    <div className="filter-column-wrapper">
      <a className="skip-main" href="#choose-activities-container">Skip to Choose Activities</a>
      <aside aria-label="Activity filters" className="filter-column">
        <section aria-labelledby="filteredResultsTitle" className="filter-section filtered-results">
          <div className="name-and-clear-wrapper">
            <h2 id="filteredResultsTitle">Filtered Results</h2>
            {clearAllButton}
          </div>
          <p>{filterCount}{filteredActivities.length} of {activities.length} activities</p>
        </section>
        {flagFilterSection}
        <ActivityClassificationFilters
          activities={activities}
          activityClassificationFilters={activityClassificationFilters}
          filterActivities={filterActivities}
          handleActivityClassificationFilterChange={handleActivityClassificationFilterChange}
          handleSavedActivityFilterChange={handleSavedActivityFilterChange}
          savedActivityFilters={savedActivityFilters}
          savedActivityIds={savedActivityIds}
        />
        {earlyAccessFilterSection}
        <TargetGradeRangeFilters
          filterActivities={filterActivities}
          handleTargetGradeRangeFilterChange={handleTargetGradeRangeFilterChange}
          targetGradeRangeFilters={targetGradeRangeFilters}
        />
        <ELLFilters
          activities={activities}
          ellFilters={ellFilters}
          filterActivities={filterActivities}
          handleELLFilterChange={handleELLFilterChange}
        />
        <TypingRequirementsFilters
          activities={activities}
          filterActivities={filterActivities}
          handleTypingRequirementFilterChange={handleTypingRequirementFilterChange}
          typingRequirementFilters={typingRequirementFilters}
        />
        {activityFocusFilterSection}
        <TopicFilters
          activities={activities}
          filterActivities={filterActivities}
          handleTopicFilterChange={handleTopicFilterChange}
          topicFilters={topicFilters}
          topics={topics}
        />
        <ContentPartnerFilters
          activities={activities}
          contentPartnerFilters={contentPartnerFilters}
          filterActivities={filterActivities}
          handleContentPartnerFilterChange={handleContentPartnerFilterChange}
        />
      </aside>
    </div>
  )
}

export default FilterColumn
